import { AuditStatus } from '@arhs-fleetback/types/entities/audits';
import { omit } from 'ramda';

import { AUDITS_API_FILTER_URL, AUDITS_FILTER_URL } from 'constants/ApiConstants';
import { AUDIT_MODEL, AUDIT_STATUS, SEARCH, STATUS } from 'constants/FilterConstants';
import { PaginationType } from 'constants/Global';
import type { MetaAction } from 'modules/global/generic.types';

import * as actionTypes from '../actionTypes';
import type * as types from '../types/dashboard';

// Dynamic dashboard filter params (dealerId, audit-model ids, search text, page, …).
type FetchAuditsInput = {
  page?: number;
  [AUDIT_MODEL]?: string[];
  [SEARCH]?: string;
  [key: string]: unknown;
};

type FetchMeta = { reset?: boolean } & MetaAction;

export const setDashboardFilter = (
  field: string,
  value: unknown,
  meta: MetaAction = {},
): types.SetDashboardFilterAction => ({
  type: actionTypes.SET_AUDITS_DASHBOARD_FILTER,
  payload: {
    field,
    value,
  },
  meta,
});

export const setDashboardFilters = (
  filters: { field: string; value: unknown }[],
  meta: MetaAction = {},
): types.SetDashboardFiltersAction => ({
  type: actionTypes.SET_AUDITS_DASHBOARD_FILTERS,
  payload: filters,
  meta,
});

export const removeDashboardFilter = (
  field: string,
  value: unknown,
  meta: MetaAction = {},
): types.RemoveDashboardFilterAction => ({
  type: actionTypes.REMOVE_AUDITS_DASHBOARD_FILTER,
  payload: {
    field,
    value,
  },
  meta,
});

export const clearDashboardFilters = (meta: MetaAction = {}): types.ClearDashboardFiltersAction => ({
  type: actionTypes.CLEAR_AUDITS_DASHBOARD_FILTERS,
  meta,
});

export const getNextPage = (
  params?: Record<string, unknown>,
  meta: MetaAction = {},
): types.GetNextPageAction => ({
  type: actionTypes.GET_NEXT_PAGE,
  payload: {
    params,
  },
  meta,
});

export const getSignedNextPage = (
  params?: Record<string, unknown>,
  meta: MetaAction = {},
): types.GetSignedNextPageAction => ({
  type: actionTypes.GET_SIGNED_NEXT_PAGE,
  payload: {
    params,
  },
  meta,
});

export const fetchAudits = (params: FetchAuditsInput, meta: FetchMeta = {}): types.FetchAuditsAction => {
  const page = meta.reset ? 0 : params.page ?? 0;

  // `period` is stripped out: it is no longer supported by the API and sending it would fail the request.
  const { [AUDIT_MODEL]: auditModelIds, [AUDIT_STATUS]: auditStatus, page: _page, period: _period, ...cleanedParams } = params;
  return {
    type: actionTypes.FETCH_AUDITS_REQUEST,
    payload: {
      request: {
        method: 'GET',
        url: AUDITS_FILTER_URL,
        params: {
          projection: 'page',
          sort: 'startDate,desc',
          paginationType: PaginationType.SLICE,
          ...cleanedParams,
          ...(auditStatus ? { [STATUS]: auditStatus as AuditStatus[] } : {}),
          ...(Array.isArray(auditModelIds) && auditModelIds.length ? { auditModelIds: auditModelIds.join(',') } : {}),
          page,
        },
      },
    },
    meta,
  };
};

export const fetchSignedAudits = (params: FetchAuditsInput, meta: FetchMeta = {}): types.FetchSignedAuditsAction => {
  const page = meta?.reset ? 0 : params?.page ?? 0;

  return {
    type: actionTypes.FETCH_AUDITS_REQUEST,
    payload: {
      request: {
        method: 'GET',
        url: AUDITS_API_FILTER_URL,
        params: {
          projection: 'page',
          sort: 'createdOn,desc',
          status: [AuditStatus.DRAFT, AuditStatus.OPEN, AuditStatus.CLOSED],
          ...omit([AUDIT_MODEL, SEARCH, 'page'], params),
          ...(params?.[SEARCH] ? { text: params[SEARCH] } : {}),
          ...(params?.[AUDIT_MODEL] ? { auditModelIds: params[AUDIT_MODEL]?.join(',') } : {}),
          page,
        },
      },
    },
    meta,
  };
};

export const fetchAuditsSuccess = (
  data: NonNullable<types.FetchAuditsSuccessAction['payload']>,
  meta: MetaAction = {},
): types.FetchAuditsSuccessAction => ({
  type: actionTypes.FETCH_AUDITS_SUCCESS,
  payload: data,
  meta,
});

export const fetchAuditsFailure = (message: string, meta: MetaAction = {}): types.FetchAuditsFailureAction => ({
  type: actionTypes.FETCH_AUDITS_FAILURE,
  payload: message,
  error: true,
  meta,
});

export const openCreation = (meta: MetaAction = {}): types.OpenCreationAction => ({
  type: actionTypes.OPEN_CREATION,
  payload: undefined,
  meta,
});

export const closeCreation = (meta: MetaAction = {}): types.CloseCreationAction => ({
  type: actionTypes.CLOSE_CREATION,
  payload: undefined,
  meta,
});

export const clearAuditsSelect = (meta: MetaAction = {}): types.ClearAuditsSelectAction => ({
  type: actionTypes.CLEAR_AUDITS_SELECT,
  meta,
});
